import { CaretLeftOutlined, CaretRightOutlined } from "@ant-design/icons";
import { Button, FormControl } from "@mui/material";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import dataSourceIcon from "../../../../assets/images/data-source-icon.svg";
import CommercialAirlineNavigationBar from "../../../../components/element/CommercialAirlineNavigationBar";
import DateLocalizationProvider from "../../../../components/element/DateLocalizationProvider";
import SearchAircraftDialog from "../../../../components/element/SearchAircraftDialog";
import SearchAirportDialog from "../../../../components/element/SearchAirportDialog";
import useAirlineAdminApi from "../../../../services/api/useAirlineAdminApi";
import { StorageContext } from "../../../../services/StorageProvider";
import { DATE_FORMAT, PAGINATION_PAGE_SIZE } from "../../../../utils/constants";
import ROUTE_PATHS from "../../../../utils/routePaths";
import "../css/AviationStackTable.css";
import AviationStackTable from "../Tables/AviationStackTable";

const AviationStackSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = PAGINATION_PAGE_SIZE;
  const { airportData } = useContext(StorageContext);
  const [selectedSourceAirports, setSelectedSourceAirports] = useState(null);
  const [selectedDestinationAirports, setSelectedDestinationAirports] =
    useState(null);
  const [selectedDepartureDate, setSelectedDepartureDate] = useState(null);
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [activeFilters, setActiveFilters] = useState({});
  const { getAviationStackData } = useAirlineAdminApi();
  const airlineId = location.state?.airlineId;
  const airlineName = location.state?.airlineName;
  const [showSearchAircraftDialog, setShowSearchAircraftDialog] =
    useState(false);
  const [showSearchSourceAirportDialog, setShowSearchSourceAirportDialog] = useState(false);
  const [showSearchDestinationAirportDialog, setShowSearchDestinationAirportDialog] = useState(false);

  const getCurrentMonthYear = () => {
    const date = new Date();
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear().toString();
    return { month, year };
  };

  const { month, year } = getCurrentMonthYear();

  const {
    data: aviationStackData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "aviation-stack-data",
      airlineId,
      month,
      year,
      currentPage,
      pageSize,
      activeFilters,
    ],
    queryFn: () =>
      getAviationStackData(
        airlineId,
        month,
        year,
        currentPage,
        pageSize,
        activeFilters
      ),
    enabled: !!airlineId,
  });

  useEffect(() => {
    if (!airlineId) {
      navigate(ROUTE_PATHS.COMMERCIAL_AIRLINE_DATA_SOURCE);
    }
    // Reset to page 0 when component mounts or airlineId changes
    setCurrentPage(0);
    // Clear sessionStorage to ensure fresh start
    sessionStorage.removeItem("aviationStackSuccessCurrentPage");
  }, [airlineId, navigate]);

  const handlePreviousPage = () => {
    setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    setCurrentPage(currentPage + 1);
  };
  const handleDepartureDateChange = (newValue) => {
    setSelectedDepartureDate(newValue);
  };
  const handleAircraftOnSelect = (aircraft) => {
    setSelectedAircraft(aircraft);
  };
  const handleSourceAirportOnSelect = (airport) => {
    setSelectedSourceAirports(airport);
  };
  const handleDestinationAirportOnSelect = (airport) => {
    setSelectedDestinationAirports(airport);
  };
  const isApplyButtonDisabled = () => {
    return !(
      selectedSourceAirports ||
      selectedDestinationAirports ||
      selectedDepartureDate ||
      selectedAircraft
    );
  };
  const handleClearFilter = () => {
    setSelectedSourceAirports(null);
    setSelectedDestinationAirports(null);
    setSelectedDepartureDate(null);
    setSelectedAircraft(null);
    setActiveFilters({});
    setCurrentPage(0);
  };
  const onClickApply = () => {
    const newFilters = {
      departureAirportId: selectedDestinationAirports?.id,
      arrivalAirportId: selectedSourceAirports?.id,
      aircraftTypeId: selectedAircraft?.id,
      flightDate: selectedDepartureDate
        ? dayjs(selectedDepartureDate).format("YYYY-MM-DD")
        : null,
    };
    setActiveFilters(newFilters);
    setCurrentPage(0);
  };

  return (
    <div className="aviation-stack-success-container">
      <CommercialAirlineNavigationBar
        title={"Flight Data Success"}
        navigationLogo={dataSourceIcon}
        fullWidthContent={true}
        backContent={
          <div className="navigation-header">
            <Link
              to={ROUTE_PATHS.COMMERCIAL_AIRLINE_HOME}
              className="navigation-header-link"
            >
              Home/
            </Link>
            <Link
              to={ROUTE_PATHS.COMMERCIAL_AIRLINE_DATA_SOURCE}
              className="navigation-header-link"
            >
              Data Source/
            </Link>
            <Link
              to={ROUTE_PATHS.COMMERCIAL_AIRLINE_DATA_SOURCE_AIRLINES}
              className="navigation-header-link"
            >
              Flight Data Success/
            </Link>
            Airline
          </div>
        }
      />
      <div className="aviation-stack-success-container">
        <div className="aviation-stack-success-table-container">
          <div className="aviation-stack-success-airport-filters-container">
            <div className="aviation-stack-success-airport-container">
              <label>Source Airport</label>
              <div
                className="aviation-stack-success-aircraft"
                onClick={() => setShowSearchSourceAirportDialog(true)}
              >
                {selectedSourceAirports ? (
                  <p>{selectedSourceAirports?.name}</p>
                ) : (
                  <span className="placeholder-text">Search</span>
                )}
              </div>
            </div>
            <DateLocalizationProvider>
              <FormControl variant="outlined">
                <label>Flight Date</label>

                <MobileDatePicker
                  id="aviation-stack-success-flight-date-datepicker"
                  className="aviation-stack-success-flight-date-datepicker"
                  format={DATE_FORMAT}
                  value={selectedDepartureDate}
                  onChange={handleDepartureDateChange}
                />
              </FormControl>
            </DateLocalizationProvider>
            <div className="aviation-stack-success-airport-container">
              <label>Destination Airport</label>
              <div
                className="aviation-stack-success-aircraft"
                onClick={() => setShowSearchDestinationAirportDialog(true)}
              >
                {selectedDestinationAirports ? (
                  <p>{selectedDestinationAirports?.name}</p>
                ) : (
                  <span className="placeholder-text">Search</span>
                )}
              </div>
            </div>
            <div className="aviation-stack-success-airport-container">
              <label>Aircraft</label>
              <div
                className="aviation-stack-success-aircraft"
                onClick={() => setShowSearchAircraftDialog(true)}
              >
                {selectedAircraft ? (
                  <p>{selectedAircraft?.icaoCode}</p>
                ) : (
                  <span className="placeholder-text">Search</span>
                )}
              </div>
            </div>

            <div className="aviation-stack-success-filter-button-container">
              <Button onClick={handleClearFilter}>Clear</Button>
              <Button
                variant="contained"
                color="secondaryButton"
                disabled={isApplyButtonDisabled()}
                onClick={onClickApply}
              >
                Apply
              </Button>
            </div>
          </div>
          <AviationStackTable
            aviationStackData={aviationStackData}
            isLoading={isLoading}
            error={error}
            isSuccess={true}
            airlineName={airlineName}
          />
          {aviationStackData?.data?.content &&
            aviationStackData?.data?.content.length > 0 && (
              <div className="pagination-container">
                <div className="pagination-content">
                  Page {currentPage + 1} of{" "}
                  {aviationStackData?.data?.totalPages}
                </div>
                <button
                  className="pagination-button"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 0}
                >
                  <CaretLeftOutlined />
                </button>
                <button
                  className="pagination-button"
                  onClick={handleNextPage}
                  disabled={
                    currentPage + 1 === aviationStackData?.data?.totalPages
                  }
                >
                  <CaretRightOutlined />
                </button>
              </div>
            )}
          <SearchAircraftDialog
            open={showSearchAircraftDialog}
            onClose={() => setShowSearchAircraftDialog(false)}
            onSelectAircraft={handleAircraftOnSelect}
          />
          <SearchAirportDialog
            open={showSearchSourceAirportDialog}
            onClose={() => setShowSearchSourceAirportDialog(false)}
            airportData={airportData}
            onSelectAirport={handleSourceAirportOnSelect}
          />
          <SearchAirportDialog
            open={showSearchDestinationAirportDialog}
            onClose={() => setShowSearchDestinationAirportDialog(false)}
            airportData={airportData}
            onSelectAirport={handleDestinationAirportOnSelect}
          />
        </div>
      </div>
    </div>
  );
};

export default AviationStackSuccess;
